import { loadStorage, removeStorage, saveStorage } from "./env.helper";
import { TokenSchema } from "../generated";
import { useMemo } from "react";
import axios from "axios";
import { BASE_PATH } from "../generated/base";

const accessTokenKey = "ACCESS_TOKEN_KEY";
type SessionToken = TokenSchema & { session_v2?: boolean; expires_in?: number; expires_at?: number };
let refreshPending: Promise<SessionToken> | null = null;

export const setToken = (token: TokenSchema) => {
  const sessionToken = token as SessionToken;
  saveStorage(accessTokenKey, JSON.stringify({
    ...sessionToken,
    expires_at: sessionToken.expires_in ? Date.now() + sessionToken.expires_in * 1000 : sessionToken.expires_at,
  }));
};

export const removeToken = () => {
  const token = getAccessToken() as SessionToken | null;
  removeStorage(accessTokenKey);
  if (token?.session_v2) {
    void axios.post(`${BASE_PATH}/token/logout`, {}, { withCredentials: true }).catch(() => undefined);
  }
};

export const getFreshAccessToken = async (): Promise<string | null> => {
  const token = getAccessToken() as SessionToken | null;
  if (!token) return null;
  if (!token.session_v2 || !token.expires_at || token.expires_at > Date.now() + 60000) {
    return token.access_token;
  }
  if (!refreshPending) {
    refreshPending = axios.post(`${BASE_PATH}/token/refresh`, {}, { withCredentials: true }).then((response) => {
      const latest = getAccessToken() as SessionToken | null;
      if (!latest) throw new Error("Session ended during refresh");
      if (latest.access_token !== token.access_token) return latest;
      setToken(response.data);
      return getAccessToken() as SessionToken;
    }).catch((error) => {
      const latest = getAccessToken() as SessionToken | null;
      if (latest && latest.access_token !== token.access_token) return latest;
      throw error;
    }).finally(() => { refreshPending = null; });
  }
  return (await refreshPending).access_token;
};

export const getAccessToken = (): TokenSchema | null => {
  const tokenJson = loadStorage(accessTokenKey);
  if (tokenJson) {
    return JSON.parse(tokenJson) as TokenSchema;
  }
  return null;
};

export const useAccessToken = (): TokenSchema | null => {
  const tokenJson = loadStorage(accessTokenKey);
  return useMemo(() => {
    if (tokenJson) {
      try {
        return JSON.parse(tokenJson) as TokenSchema;
      } catch (error) {
        console.warn(error);
        removeStorage(accessTokenKey);
      }
    }
    return null;
  }, [tokenJson]);
};
